/**
 * Infographic-to-lesson matching utility.
 * Normalizes filenames and compares against lesson/course names
 * to find the best match with a confidence score.
 */

interface LessonRecord {
  lesson_id: string;
  lesson_name: string;
  course_name: string;
  lesson_order: number;
}

export interface MatchResult {
  lessonId: string;
  lessonName: string;
  courseName: string;
  confidence: "high" | "medium" | "low" | "none";
  score: number;
}

/** Normalize a string for comparison: lowercase, remove punctuation, collapse whitespace */
function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/\.[^.]+$/, "") // remove file extension
    .replace(/[-_]/g, " ")   // dashes/underscores → spaces
    .replace(/[^a-z0-9 ]/g, "") // remove remaining punctuation
    .replace(/\s+/g, " ")
    .trim();
}

/** Simple token-based similarity (Jaccard-like) */
function tokenSimilarity(a: string, b: string): number {
  const tokensA = new Set(a.split(" ").filter(Boolean));
  const tokensB = new Set(b.split(" ").filter(Boolean));
  if (tokensA.size === 0 || tokensB.size === 0) return 0;
  let intersection = 0;
  for (const t of tokensA) {
    if (tokensB.has(t)) intersection++;
  }
  const union = new Set([...tokensA, ...tokensB]).size;
  return intersection / union;
}

/** Check if one string contains the other */
function containsMatch(a: string, b: string): boolean {
  return a.includes(b) || b.includes(a);
}

/**
 * Find the best matching lesson for a given filename.
 * Returns the top match with confidence level.
 */
export function findBestMatch(
  fileName: string,
  lessons: LessonRecord[]
): MatchResult | null {
  if (!lessons.length) return null;

  const normFile = normalize(fileName);
  
  let bestScore = 0;
  let bestLesson: LessonRecord | null = null;

  for (const lesson of lessons) {
    const normLesson = normalize(lesson.lesson_name);
    const normCourse = normalize(lesson.course_name);
    const normCombined = `${normCourse} ${normLesson}`;

    let score = 0;

    // Exact lesson name match
    if (normFile === normLesson) {
      score = 1.0;
    }
    // Exact combined match
    else if (normFile === normCombined) {
      score = 0.95;
    }
    // File contains full lesson name
    else if (containsMatch(normFile, normLesson) && normLesson.length > 3) {
      score = 0.85;
    }
    // File contains course + partial lesson
    else if (normFile.includes(normCourse) && normCourse.length > 3) {
      // Course matched, check lesson token overlap
      const lessonSim = tokenSimilarity(normFile, normLesson);
      score = 0.5 + lessonSim * 0.4;
    }
    // Token similarity
    else {
      const combinedSim = tokenSimilarity(normFile, normCombined);
      const lessonSim = tokenSimilarity(normFile, normLesson);
      score = Math.max(combinedSim, lessonSim);
    }

    if (score > bestScore) {
      bestScore = score;
      bestLesson = lesson;
    }
  }

  if (!bestLesson) return null;

  let confidence: "high" | "medium" | "low" | "none";
  if (bestScore >= 0.8) confidence = "high";
  else if (bestScore >= 0.5) confidence = "medium";
  else if (bestScore >= 0.25) confidence = "low";
  else confidence = "none";

  return {
    lessonId: bestLesson.lesson_id,
    lessonName: bestLesson.lesson_name,
    courseName: bestLesson.course_name,
    confidence,
    score: bestScore,
  };
}

/**
 * Batch match multiple filenames against lessons.
 */
export function batchMatch(
  fileNames: string[],
  lessons: LessonRecord[]
): Map<string, MatchResult | null> {
  const results = new Map<string, MatchResult | null>();
  for (const fn of fileNames) {
    results.set(fn, findBestMatch(fn, lessons));
  }
  return results;
}
